import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { CATEGORY_COLORS, formatBRL } from "../lib/constants";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, CartesianGrid } from "recharts";
import { toast } from "sonner";

const MONTHS = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

export default function ReportsPage() {
  const { api } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [monthOffset, setMonthOffset] = useState(0);

  useEffect(() => {
    api.get("/transactions").then((r) => setItems(r.data)).catch(() => toast.error("Erro")).finally(() => setLoading(false));
  }, [api]);

  const now = new Date();
  const target = useMemo(
    () => new Date(now.getFullYear(), now.getMonth() + monthOffset, 1),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [monthOffset]
  );
  const targetLabel = `${MONTHS[target.getMonth()]} ${target.getFullYear()}`;

  const monthItems = useMemo(
    () => items.filter((t) => {
      const d = new Date(t.date);
      return d.getMonth() === target.getMonth() && d.getFullYear() === target.getFullYear();
    }),
    [items, target]
  );

  const monthly = useMemo(() => {
    const map = {};
    for (const t of items) {
      const d = new Date(t.date);
      const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      map[k] = map[k] || { name: `${MONTHS[d.getMonth()]}/${String(d.getFullYear()).slice(2)}`, entrada: 0, saida: 0, ts: d.getTime() };
      if (t.type === "income") map[k].entrada += t.amount;
      else map[k].saida += t.amount;
    }
    return Object.values(map).sort((a, b) => a.ts - b.ts).slice(-6);
  }, [items]);

  const catData = useMemo(() => {
    const map = {};
    for (const t of monthItems.filter((t) => t.type === "expense")) {
      map[t.category] = (map[t.category] || 0) + t.amount;
    }
    return Object.entries(map).map(([name, value]) => ({ name, value, color: CATEGORY_COLORS[name] || "#94A3B8" }));
  }, [monthItems]);

  const income = monthItems.filter((t) => t.type === "income").reduce((s, t) => s + t.amount, 0);
  const expense = monthItems.filter((t) => t.type === "expense").reduce((s, t) => s + t.amount, 0);

  return (
    <div className="max-w-md mx-auto px-5 pt-8 pb-6 space-y-5">
      <header>
        <p className="text-[10px] uppercase tracking-[0.2em] text-emerald-300 font-semibold">Relatórios</p>
        <h1 className="font-display text-2xl font-bold text-white">Resumo Mensal</h1>
      </header>

      <div className="flex items-center justify-between pnc-card p-3">
        <button data-testid="month-prev" onClick={() => setMonthOffset((v) => v - 1)} className="px-3 py-1.5 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800/60">‹ Anterior</button>
        <span data-testid="month-label" className="font-semibold text-white">{targetLabel}</span>
        <button data-testid="month-next" onClick={() => setMonthOffset((v) => Math.min(0, v + 1))} disabled={monthOffset >= 0} className="px-3 py-1.5 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800/60 disabled:opacity-40">Próximo ›</button>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <div className="pnc-card p-3 text-center">
          <p className="text-[10px] uppercase tracking-wider text-slate-400">Entradas</p>
          <p className="text-sm font-mono-nums font-bold text-emerald-400 mt-1">{formatBRL(income)}</p>
        </div>
        <div className="pnc-card p-3 text-center">
          <p className="text-[10px] uppercase tracking-wider text-slate-400">Saídas</p>
          <p className="text-sm font-mono-nums font-bold text-rose-400 mt-1">{formatBRL(expense)}</p>
        </div>
        <div className="pnc-card p-3 text-center">
          <p className="text-[10px] uppercase tracking-wider text-slate-400">Saldo</p>
          <p className="text-sm font-mono-nums font-bold text-white mt-1">{formatBRL(income - expense)}</p>
        </div>
      </div>

      <div className="pnc-card p-4">
        <h3 className="font-display font-semibold text-white mb-3">Entradas vs Saídas (últimos 6 meses)</h3>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={monthly}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="name" stroke="#94A3B8" fontSize={10} tickLine={false} axisLine={false} />
              <YAxis stroke="#94A3B8" fontSize={10} tickLine={false} axisLine={false} tickFormatter={(v) => `R$${v}`} />
              <Tooltip contentStyle={{ background: "#1E293B", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12 }} labelStyle={{ color: "#F8FAFC" }} formatter={(v, k) => [formatBRL(v), k === "entrada" ? "Entradas" : "Saídas"]} />
              <Bar dataKey="entrada" fill="#10B981" radius={[6, 6, 0, 0]} />
              <Bar dataKey="saida" fill="#F43F5E" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="pnc-card p-4">
        <h3 className="font-display font-semibold text-white mb-3">Gastos por categoria ({targetLabel})</h3>
        {catData.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-10">Sem gastos neste mês.</p>
        ) : (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={catData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90} paddingAngle={2}>
                  {catData.map((c, i) => <Cell key={i} fill={c.color} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#1E293B", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12 }} formatter={(v) => formatBRL(v)} />
                <Legend wrapperStyle={{ fontSize: 11, color: "#94A3B8" }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {loading && <p className="text-center text-slate-500 text-sm">Carregando…</p>}
    </div>
  );
}
